import { motion } from 'framer-motion';
import { OrderFormData } from '@/types/order';
import { Card } from '@/components/ui/card';
import { Check } from 'lucide-react';

// Labels pour l'affichage
const typeVenteLabels: Record<string, string> = {
  gre_a_gre: 'Gré à gré',
  biddit: 'Biddit',
  vente_publique: 'Vente publique',
  location: 'Location',
  autre: 'Autre',
};

const typeBienLabels: Record<string, string> = {
  maison: 'Maison',
  appartement: 'Appartement',
  immeuble: 'Immeuble',
  terrain: 'Terrain',
  commerce: 'Commerce',
  autre: 'Autre',
};

const occupationLabels: Record<string, string> = {
  vide: 'Vide',
  locataire: 'Occupé par locataire',
  proprietaire: 'Occupé par propriétaire',
};

const modeAccesLabels: Record<string, string> = {
  cles_etude: "Clés à l'étude",
  code: "Code d'accès",
  cles_ailleurs: 'Clés à récupérer ailleurs',
};

const marketingTypeLabels: Record<string, string> = {
  bien: 'Bien immobilier',
  terrain: 'Terrain',
};

const marketingUpgradeLabels: Record<string, string> = {
  video: 'Vidéo immobilière',
  '360': 'Visite virtuelle 360°',
};

const controleLabels: Record<string, string> = {
  peb: 'Certificat PEB',
  elec: 'Contrôle électricité',
};

interface Step7RecapitulatifProps {
  data: OrderFormData;
  onChange: (data: Partial<OrderFormData>) => void;
}

export function Step7Recapitulatif({ data, onChange }: Step7RecapitulatifProps) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <div className="space-y-2">
        <h2 className="text-3xl font-display font-bold text-navy-600">
          Récapitulatif de votre commande
        </h2>
        <p className="text-navy-400">
          Vérifiez les informations avant l'envoi
        </p>
      </div>

      <div className="space-y-6">
        {/* Contexte (uniquement si Pack Visites) */}
        {data.packVisites && (
          <Card>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-navy-600 mb-4">Contexte de vente</h3>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-navy-400">Type de vente</dt>
                  <dd className="text-navy-600 font-medium">{typeVenteLabels[data.typeVente] || data.typeVente}</dd>
                </div>
                {data.deadline && (
                  <div>
                    <dt className="text-navy-400">Date limite</dt>
                    <dd className="text-navy-600 font-medium">{data.deadline}</dd>
                  </div>
                )}
              </dl>
            </div>
          </Card>
        )}

        {/* Services */}
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-navy-600 mb-4">Services sélectionnés</h3>
            <div className="space-y-2">
              {data.packVisites && (
                <div className="flex items-center gap-2 text-navy-600">
                  <Check className="w-4 h-4 text-gold-600" />
                  <span>Pack Visites (2 mois, panneau inclus)</span>
                </div>
              )}
              {data.packMarketing && (
                <div className="flex items-center gap-2 text-navy-600">
                  <Check className="w-4 h-4 text-gold-600" />
                  <span>
                    Pack Marketing — {marketingTypeLabels[data.marketingType || 'bien']}
                    {data.marketingUpgrade && ` + ${marketingUpgradeLabels[data.marketingUpgrade]}`}
                    {data.marketingDrone && ' + Photos drone'}
                    {data.marketingType === 'terrain' && ' (drone inclus)'}
                  </span>
                </div>
              )}
              {data.packControles.length > 0 && (
                <div className="flex items-center gap-2 text-navy-600">
                  <Check className="w-4 h-4 text-gold-600" />
                  <span>
                    Pack Contrôles — {data.packControles.map(c => controleLabels[c]).join(', ')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Donneur d'ordre */}
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-navy-600 mb-4">Donneur d'ordre</h3>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-navy-400">Société</dt>
                <dd className="text-navy-600 font-medium">{data.societe}</dd>
              </div>
              <div>
                <dt className="text-navy-400">Contact</dt>
                <dd className="text-navy-600 font-medium">{data.nomPrenom}</dd>
              </div>
              <div>
                <dt className="text-navy-400">Email</dt>
                <dd className="text-navy-600 font-medium">{data.email}</dd>
              </div>
              <div>
                <dt className="text-navy-400">Téléphone</dt>
                <dd className="text-navy-600 font-medium">{data.telephone}</dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-navy-400">Adresse</dt>
                <dd className="text-navy-600 font-medium">
                  {data.adresseDonneurRue}, {data.adresseDonneurCP} {data.adresseDonneurVille}
                </dd>
              </div>
            </dl>
          </div>
        </Card>

        {/* Bien */}
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-navy-600 mb-4">Bien immobilier</h3>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-navy-400">Type</dt>
                <dd className="text-navy-600 font-medium">{typeBienLabels[data.typeBien] || data.typeBien}</dd>
              </div>
              <div>
                <dt className="text-navy-400">Occupation</dt>
                <dd className="text-navy-600 font-medium">{occupationLabels[data.occupation] || data.occupation}</dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-navy-400">Adresse</dt>
                <dd className="text-navy-600 font-medium">{data.adresseBien}</dd>
              </div>
            </dl>
          </div>
        </Card>

        {/* Contact occupant */}
        {data.occupantNom && (
          <Card>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-navy-600 mb-4">Contact occupant</h3>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-navy-400">Nom</dt>
                  <dd className="text-navy-600 font-medium">{data.occupantNom}</dd>
                </div>
                <div>
                  <dt className="text-navy-400">Téléphone</dt>
                  <dd className="text-navy-600 font-medium">{data.occupantTelephone}</dd>
                </div>
                {data.occupantEmail && (
                  <div>
                    <dt className="text-navy-400">Email</dt>
                    <dd className="text-navy-600 font-medium">{data.occupantEmail}</dd>
                  </div>
                )}
              </dl>
            </div>
          </Card>
        )}

        {/* Accès */}
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-navy-600 mb-4">Accès au bien</h3>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-navy-400">Mode d'accès</dt>
                <dd className="text-navy-600 font-medium">{modeAccesLabels[data.modeAcces] || data.modeAcces}</dd>
              </div>
              {data.modeAcces === 'code' && data.codeAcces && (
                <div>
                  <dt className="text-navy-400">Code</dt>
                  <dd className="text-navy-600 font-medium">{data.codeAcces}</dd>
                </div>
              )}
              {data.modeAcces === 'cles_ailleurs' && (
                <>
                  <div className="md:col-span-2">
                    <dt className="text-navy-400">Adresse de retrait</dt>
                    <dd className="text-navy-600 font-medium">{data.adresseRetraitCles}</dd>
                  </div>
                  <div>
                    <dt className="text-navy-400">Contact</dt>
                    <dd className="text-navy-600 font-medium">{data.contactRetraitClesNom}</dd>
                  </div>
                  <div>
                    <dt className="text-navy-400">Téléphone</dt>
                    <dd className="text-navy-600 font-medium">{data.contactRetraitClesTelephone}</dd>
                  </div>
                </>
              )}
            </dl>
          </div>
        </Card>

        {/* Consentements */}
        <Card>
          <div className="p-6 space-y-4">
            <h3 className="text-lg font-semibold text-navy-600 mb-4">Confirmations</h3>

            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={data.confirmProfessionnel}
                  onChange={(e) => onChange({ confirmProfessionnel: e.target.checked })}
                  className="w-5 h-5 rounded border-2 border-navy-300 text-gold-600
                           focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 cursor-pointer"
                />
              </div>
              <span className="text-navy-600 text-sm group-hover:text-navy-700 flex-1">
                Je confirme être un professionnel (notaire, avocat, agent immobilier)
                et commander ce service dans le cadre de mon activité professionnelle.
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={data.acceptConditions}
                  onChange={(e) => onChange({ acceptConditions: e.target.checked })}
                  className="w-5 h-5 rounded border-2 border-navy-300 text-gold-600
                           focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 cursor-pointer"
                />
              </div>
              <span className="text-navy-600 text-sm group-hover:text-navy-700 flex-1">
                J'accepte les{' '}
                <a
                  href="/conditions-generales"
                  target="_blank"
                  className="text-gold-600 hover:text-gold-700 underline"
                >
                  conditions générales d'utilisation
                </a>
                .
              </span>
            </label>
          </div>
        </Card>
      </div>
    </motion.div>
  );
}
